import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";

const REPORTS = [
  {
    id: "venta-5-anos",
    title: "Preparar Stage para venta en 5 años",
    subtitle: "Informe Executive Chair · Jul 2026",
    href: `${process.env.PUBLIC_URL || ""}/informes/Informe-CEO-Stage-Venta-5-anos.html`,
  },
];

export default function InformesECPage() {
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [activeId, setActiveId] = useState(REPORTS[0]?.id || null);

  const activeReport = useMemo(
    () => REPORTS.find((r) => r.id === activeId) || REPORTS[0],
    [activeId],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login", { replace: true });
          return;
        }
        const { userId } = jwtDecode(token);
        const user = await getUserById(userId);
        const perms = mapUserToPermissionState(user);
        if (cancelled) return;
        if (!perms.Permiso_InformesEC && !perms.isAdmin) {
          setAllowed(false);
          return;
        }
        setAllowed(true);
      } catch {
        if (!cancelled) setAllowed(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          ml: contentMargin,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ ml: contentMargin, p: 3 }}>
        <Alert severity="warning">
          No tienes permiso para ver Informes EC. Activa{" "}
          <strong>Permiso_InformesEC</strong> en Users Permissions.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ml: contentMargin,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          minHeight: 56,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            borderRight: 1,
            borderColor: "divider",
            minWidth: 220,
          }}
        >
          <AssessmentOutlinedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Informes EC
          </Typography>
        </Box>
        <List
          dense
          disablePadding
          sx={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            overflowX: "auto",
          }}
        >
          {REPORTS.map((report) => (
            <ListItemButton
              key={report.id}
              selected={report.id === activeReport?.id}
              onClick={() => setActiveId(report.id)}
              sx={{ maxWidth: 360 }}
            >
              <ListItemText
                primary={report.title}
                secondary={report.subtitle}
                primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                secondaryTypographyProps={{ noWrap: true, fontSize: 11 }}
              />
            </ListItemButton>
          ))}
        </List>
        {activeReport && (
          <ListItemButton
            component="a"
            href={activeReport.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flex: "0 0 auto", px: 2 }}
          >
            <OpenInNewOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Abrir</Typography>
          </ListItemButton>
        )}
      </Box>

      {activeReport ? (
        <Box
          component="iframe"
          title={activeReport.title}
          src={activeReport.href}
          sx={{
            flex: 1,
            width: "100%",
            border: 0,
            bgcolor: "#fff",
          }}
        />
      ) : (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">No hay informes publicados todavía.</Alert>
        </Box>
      )}
    </Box>
  );
}
